// Inline booth-category chip + editor used on approved application cards.
// Free-form text so organizers can name their groupings whatever fits the
// event (Food, Kids, Wellness, Music, etc.). Suggestions from other
// approved vendors on the same event show up as a datalist for one-tap reuse.
import { useState } from "react";
import { Tag, X, Check } from "lucide-react";

export default function BoothCategoryEditor({
  applicationId,
  currentCategory,
  suggestions = [],
  onChange,
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentCategory || "");
  const listId = `booth-cat-suggestions-${applicationId}`;

  const commit = async () => {
    const next = value.trim();
    if (next === (currentCategory || "")) {
      setEditing(false);
      return;
    }
    await onChange(next);
    setEditing(false);
  };

  const clear = async () => {
    setValue("");
    await onChange("");
    setEditing(false);
  };

  if (editing) {
    // Preset chips shown above the input so common categories are one tap
    // away instead of a full retype. Dedupe against the current value so
    // the active category isn't offered as a redundant chip.
    const uniqueSuggestions = Array.from(new Set(suggestions))
      .filter((s) => s && s.toLowerCase() !== (currentCategory || "").toLowerCase())
      .slice(0, 8);
    return (
      <div className="space-y-2" data-testid={`booth-category-editor-${applicationId}`}>
        <div className="inline-flex items-center gap-1.5 flex-wrap">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value.slice(0, 48))}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(); }
              if (e.key === "Escape") { setValue(currentCategory || ""); setEditing(false); }
            }}
            list={listId}
            autoFocus
            placeholder="Food, Kids, Crafts…"
            maxLength={48}
            data-testid={`booth-category-input-${applicationId}`}
            className="text-xs px-2 py-1 rounded-full border outline-none focus:ring-2"
            style={{
              borderColor: "rgba(107,47,186,0.4)",
              background: "#fff",
              minWidth: 140,
            }}
          />
          <datalist id={listId}>
            {suggestions.map((s) => <option key={s} value={s} />)}
          </datalist>
          <button
            type="button"
            onClick={commit}
            data-testid={`booth-category-save-${applicationId}`}
            className="p-1 rounded-full"
            style={{ background: "var(--avt-purple)", color: "#fff" }}
            aria-label="Save category"
          >
            <Check size={12} />
          </button>
          {currentCategory && (
            <button
              type="button"
              onClick={clear}
              data-testid={`booth-category-clear-${applicationId}`}
              className="p-1 rounded-full opacity-60 hover:opacity-100"
              aria-label="Clear category"
            >
              <X size={12} />
            </button>
          )}
        </div>
        {uniqueSuggestions.length > 0 && (
          <div className="flex flex-wrap gap-1" data-testid={`booth-category-presets-${applicationId}`}>
            <span className="text-[10px] uppercase tracking-widest self-center opacity-60">
              Use:
            </span>
            {uniqueSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={async () => { setValue(s); await onChange(s); setEditing(false); }}
                data-testid={`booth-category-preset-${applicationId}-${s.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[11px] px-2 py-0.5 rounded-full transition hover:opacity-80"
                style={{
                  background: "rgba(107,47,186,0.08)",
                  color: "var(--avt-purple)",
                }}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid={`booth-category-chip-${applicationId}`}
      className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full transition hover:opacity-90"
      style={{
        background: currentCategory
          ? "rgba(107,47,186,0.14)"
          : "rgba(0,0,0,0.05)",
        color: currentCategory ? "var(--avt-purple)" : "var(--ink-soft)",
      }}
      title={currentCategory ? "Change booth category" : "Add booth category"}
    >
      <Tag size={11} />
      {currentCategory || "Add category"}
    </button>
  );
}
